import { request } from './client';
import type {
    LoyaltyConfiguration,
    LoyaltyOffers,
    LoyaltyRedemption,
    LoyaltySignInCompleted,
    LoyaltySignInStarted,
} from './types';

/**
 * Whether to ask this guest about loyalty at all, and what the screen says.
 *
 * The location comes from the token, so there is nothing for the caller to pass and nothing it could get
 * wrong.
 */
export function getLoyaltyConfiguration(token: string, signal?: AbortSignal): Promise<LoyaltyConfiguration> {
    return request<LoyaltyConfiguration>('/loyalty', { token, signal });
}

/**
 * Offers the phone number or email the guest typed.
 *
 * Always called, even for a brand that does not verify — that brand answers `verificationRequired: false` and
 * the flow goes straight on. Branching on a configuration flag here instead would put the rule in two places.
 */
export function startLoyaltySignIn(
    token: string,
    identifier: string,
    signal?: AbortSignal,
): Promise<LoyaltySignInStarted> {
    return request<LoyaltySignInStarted>('/loyalty/sign-in', {
        method: 'POST',
        token,
        body: { identifier },
        signal,
    });
}

/** Answers the code. A wrong one is `verified: false`, not an error — see `LoyaltySignIn`. */
export function completeLoyaltySignIn(
    token: string,
    challengeId: string,
    code: string,
    signal?: AbortSignal,
): Promise<LoyaltySignInCompleted> {
    return request<LoyaltySignInCompleted>('/loyalty/sign-in/verify', {
        method: 'POST',
        token,
        body: { challengeId, code },
        signal,
    });
}

/**
 * What this guest can redeem against this check.
 *
 * POST rather than GET because the account id is the guest's phone number or email address, and URLs end up
 * in logs, referrers and history.
 */
export function getLoyaltyOffers(
    token: string,
    accountId: string,
    signInToken: string | null,
    signal?: AbortSignal,
): Promise<LoyaltyOffers> {
    return request<LoyaltyOffers>('/loyalty/offers', {
        method: 'POST',
        token,
        body: { accountId, signInToken },
        signal,
    });
}

/**
 * Redeems what the guest chose and puts the discount on the check.
 *
 * Sends ids, never amounts. The API re-reads the offers from the vendor and matches by id, so nothing here
 * can misstate what a reward is worth — see the note in GuestPayAPI's LoyaltyService.
 */
export function redeemLoyalty(
    token: string,
    accountId: string,
    signInToken: string | null,
    redeemableIds: string[],
    signal?: AbortSignal,
): Promise<LoyaltyRedemption> {
    return request<LoyaltyRedemption>('/loyalty/redeem', {
        method: 'POST',
        token,
        body: { accountId, signInToken, redeemableIds },
        signal,
    });
}
